import { useEffect, useMemo } from "react";
import { useGrowthIndicator } from "../hooks";
import { StatusOptionData } from "../interfaces";

export const dynamicConstants = () => {
    // Fetch growth indicator settings
    const { data: growthIndicatorData } = useGrowthIndicator();

    // Prefer server value, then persisted value (from previous session), then default
    const persisted = useMemo(() => {
        try {
            const raw = localStorage.getItem("growth_indicator_range");
            return raw ? (JSON.parse(raw) as [number, number]) : undefined;
        } catch {
            return undefined;
        }
    }, []);

    const growthIndicatorRange = useMemo<[number, number]>(() => {
        return (
            growthIndicatorData?.growth_indicators ||
            persisted ||
            [0, 50]
        );
    }, [growthIndicatorData, persisted]);

    // Persist latest fetched value so refresh uses correct data instead of default
    useEffect(() => {
        if (growthIndicatorData?.growth_indicators) {
            try {
                localStorage.setItem(
                    "growth_indicator_range",
                    JSON.stringify(growthIndicatorData.growth_indicators)
                );
            } catch {
                // ignore storage errors (e.g., quota)
            }
        }
    }, [growthIndicatorData]);

    const [minThreshold, maxThreshold] = growthIndicatorRange;

    const DYNAMIC_STATUS_OPTIONS: StatusOptionData[] = useMemo(() => (
        [
            { label: "All Status", value: "all", color: "gray" },
            { label: `Growth (TN/TR > ${maxThreshold})`, value: "success", color: "green" },
            { label: `Stable (${minThreshold} ≤ TN/TR ≤ ${maxThreshold})`, value: "warning", color: "yellow" },
            { label: `Declining (TN/TR < ${minThreshold})`, value: "danger", color: "red" },
        ] as StatusOptionData[]
    ), [minThreshold, maxThreshold]);

    return {
        growthIndicatorRange,
        DYNAMIC_STATUS_OPTIONS,
    };
}
